import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'

interface AvatarProps {
  size?: 'small' | 'medium' | 'large'
  className?: string
  onClick?: () => void
}

const sizeConfig = {
  small: { width: 32, height: 32, fontSize: 14 },
  medium: { width: 40, height: 40, fontSize: 16 },
  large: { width: 64, height: 64, fontSize: 24 }
}

/**
 * UNIFIED Avatar component for the entire platform
 * Priority: 1. Uploaded avatar → 2. Google OAuth avatar → 3. User initials
 */
export function Avatar({ size = 'medium', className = '', onClick }: AvatarProps) {
  const { user, isLoading } = useAuth()
  const [imageError, setImageError] = useState(false)
  const { width, height, fontSize } = sizeConfig[size]

  // Reset image error when user changes
  useEffect(() => {
    setImageError(false)
  }, [user?.id])

  // Loading state
  if (isLoading) {
    return (
      <div
        className={className}
        onClick={onClick}
        style={{
          width,
          height,
          borderRadius: '50%',
          backgroundColor: 'rgb(var(--tr-paper-200))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      />
    )
  }

  // Get user initials fallback
  const getInitials = () => {
    if (user?.name) return user.name.charAt(0).toUpperCase()
    if (user?.email) return user.email.charAt(0).toUpperCase()
    return 'U'
  }

  // Priority 1: User uploaded avatar
  if (user?.profilePicture && !imageError) {
    const avatarUrl = user.profilePicture.startsWith('http')
      ? user.profilePicture
      : `${import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'http://localhost:8000'}${user.profilePicture}`

    return (
      <img
        src={avatarUrl}
        alt={`${user.name || user.email || 'User'}'s avatar`}
        className={className}
        onClick={onClick}
        onError={() => setImageError(true)}
        style={{
          width,
          height,
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
          border: '1px solid rgba(0,0,0,0.1)'
        }}
      />
    )
  }

  // Priority 2 & 3: Fallback to initials (Google OAuth avatar would be in profilePicture field)
  return (
    <div
      className={className}
      onClick={onClick}
      style={{
        width,
        height,
        borderRadius: '50%',
        backgroundColor: 'rgb(var(--tr-accent-500))',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize,
        fontWeight: '600',
        flexShrink: 0,
        cursor: onClick ? 'pointer' : 'default'
      }}
    >
      {getInitials()}
    </div>
  )
}